import React, { Fragment } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";

const createHelpers = (width, height, css) => {
    // somehow sizes is ending up in markup, even if it is not a valid svg attribute
    // until we have a better solution, just render it empty, instead to '[Object object]'
    const sanitizeSizes = sizes =>
        Object.defineProperty(sizes, "toString", {
            value: () => "",
            enumerable: false
        });

    const getDimensions = (size, sizes) => {
        if (
            size &&
            typeof size.width === "number" &&
            typeof size.height === "number"
        ) {
            return size;
        }

        return size && sizes[size] ? sizes[size] : { width, height };
    };

    const getCss = (size, sizes, fillColor, fillColorRule, noStyles) => {
        if (noStyles) {
            return "";
        }

        const dimensions = getDimensions(size, sizes);
        const fillRule =
            fillColor && fillColorRule
                ? `${fillColorRule}{ fill: ${fillColor}; }`
                : "";

        return css`
            width: ${dimensions.width}px;
            height: ${dimensions.height}px;
            ${fillRule}
        `;
    };

    const propsToCss = ({ size, sizes, fillColor, fillColorRule, noStyles }) =>
        getCss(size, sizes, fillColor, fillColorRule, noStyles);

    return {
        getCss,
        getDimensions,
        propsToCss,
        sanitizeSizes
    };
};

const width = "20";
const height = "20";
const viewBox = "0 0 20 20";

const { getDimensions, getCss, propsToCss, sanitizeSizes } = createHelpers(
    width,
    height,
    css
);

const sizes = sanitizeSizes({
    small: { width: 17, height: 17 },
    medium: { width: 24, height: 24 }
});

const Image = styled.svg`
    ${propsToCss}
`;

const children = (
    <Fragment>
        <path
            d="M10 0C4.477 0 0 4.477 0 10s4.477 10 10 10 10-4.477 10-10S15.523 0 10 0zm7.489 9.905a12.246 12.246 0 00-4.39-.077c-.221-.496-.46-1-.71-1.507 1.372-.6 2.712-1.373 3.912-2.372a7.467 7.467 0 011.188 3.955zm-7.5-7.419c1.925 0 3.673.73 5 1.921-.619.626-1.697 1.441-3.515 2.186-.792-1.402-1.7-2.775-2.74-3.993.409-.07.827-.114 1.255-.114zm-3.108.68a43.263 43.263 0 012.707 4.083c-1.773.521-4.043.948-6.934 1.166a7.519 7.519 0 014.227-5.249zM4.26 14.831a7.46 7.46 0 01-1.759-4.475c1.673-.039 4.822-.269 8.061-1.338.214.414.425.839.629 1.277-3.189 1.042-5.599 3.142-6.931 4.536zm5.729 2.664a7.46 7.46 0 01-4.254-1.326c2.102-2.278 4.289-3.458 6.247-4.024.571 1.47 1.038 3.016 1.294 4.585a7.453 7.453 0 01-3.287.765zm5.183-2.084a49.391 49.391 0 00-1.267-3.654 11.667 11.667 0 013.347.09 7.504 7.504 0 01-2.08 3.564z"
            key="key-0"
        />
    </Fragment>
);

const defaultProps = {
    children,
    viewBox,
    fillColor: null,
    fillColorRule: "&&& path, &&& use, &&& g",
    sizes,
    size: null
};

Image.propTypes /* remove-proptypes */ = {
    fillColor: PropTypes.string,
    fillColorRule: PropTypes.string,
    viewBox: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    size: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            height: PropTypes.number.isRequired,
            width: PropTypes.number.isRequired
        })
    ]),
    sizes: PropTypes.shape({
        height: PropTypes.number,
        width: PropTypes.number
    })
};

export default Object.assign(Image, {
    getDimensions,
    getCss,
    defaultProps,
    displayName: "Dribbble"
});
